'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import FeaturedArticle from '@/app/law-resource-hub/components/FeaturedArticle';
import BlogPostCard from '@/app/law-resource-hub/components/BlogPostCard';
import LegalResourceCard from '@/app/law-resource-hub/components/LegalResourceCard';
import { createClient } from '@/lib/supabase/client';

export type BlogPost = {
  id: string;
  category: 'Immigration Law' | 'Civic Rights' | 'Advocacy Guides' | 'Legal Aid';
  type: 'Article' | 'Guide' | 'Q&A' | 'Analysis';
  title: string;
  excerpt: string;
  fullContent: string;
  author: string;
  authorRole: string;
  school: string;
  date: string;
  readTime: string;
  tags: string[];
  featured?: boolean;
};

export type LegalResource = {
  id: string;
  category: 'Immigration Law' | 'Civic Rights' | 'Advocacy Guides' | 'Legal Aid';
  title: string;
  description: string;
  type: 'PDF Guide' | 'Know Your Rights' | 'FAQ' | 'Template';
  lastUpdated: string;
  downloads: number;
  language: string[];
};

const blogPosts: BlogPost[] = [
  {
    id: 'post-001',
    category: 'Immigration Law',
    type: 'Article',
    title: 'Understanding Your Rights During Immigration Proceedings',
    excerpt:
      'If you or someone you know is facing immigration proceedings, knowing your legal rights can make a critical difference. This guide breaks down the key protections available to non-citizens in the U.S. legal system.',
    fullContent: `Immigration proceedings can be one of the most stressful experiences a family faces. Whether you are a visa holder, an asylum seeker, or an undocumented individual, the U.S. legal system provides certain protections that apply regardless of your immigration status.

The Fifth Amendment to the U.S. Constitution guarantees due process to all "persons" — not just citizens. This means that before the government can remove you from the country, you are entitled to a hearing before an immigration judge. You have the right to present evidence, cross-examine witnesses, and appeal an unfavorable decision.

One of the most important rights is the right to remain silent. If you are stopped by immigration authorities, you are not required to answer questions about your immigration status, where you were born, or how you entered the country. You may simply say, "I am exercising my right to remain silent." This is not an admission of guilt — it is a constitutional protection.

You also have the right to an attorney, though unlike in criminal proceedings, the government is not required to provide one for free. Many nonprofit organizations and legal aid clinics offer free or low-cost representation to immigrants. If you are detained, you have the right to contact a lawyer and to make phone calls.

If immigration officers come to your home, you do not have to open the door unless they have a judicial warrant — one signed by a judge, not just an administrative warrant from ICE. Ask them to slide the warrant under the door and check that it has a judge's signature and your correct name and address.

Understanding these rights before an encounter occurs is critical. Share this information with your family and community. Knowledge is the first line of defense.`,
    author: 'Maya Okonkwo',
    authorRole: 'Student Writer',
    school: 'Howard University',
    date: 'Jul 14, 2026',
    readTime: '8 min read',
    tags: ['Due Process', 'Detention Rights', 'Legal Representation'],
    featured: true,
  },
  {
    id: 'post-002',
    category: 'Civic Rights',
    type: 'Guide',
    title: 'How to Engage Your Local Representatives Effectively',
    excerpt:
      'Many young people feel their voices don\'t reach elected officials. This step-by-step guide shows you exactly how to contact, meet with, and influence your local representatives on issues that matter to you.',
    fullContent: `Elected officials work for you — but only if you make your voice heard. Many young people assume their opinions don't matter to politicians, but the reality is that constituent contact is one of the most powerful tools in democracy. Here's how to use it.

**Step 1: Know Who Represents You**
Start by identifying your local, state, and federal representatives. Visit usa.gov/elected-officials and enter your zip code. You'll find your city council members, state legislators, and U.S. Congress members. Each of them has a staff dedicated to constituent services.

**Step 2: Choose Your Issue**
Pick one specific issue you care about. Broad concerns like "fix the environment" are harder to act on than specific asks like "support House Bill 1234, which funds clean energy in our district." The more specific your ask, the more actionable it is for a legislator.

**Step 3: Call, Don't Just Email**
Phone calls are more effective than emails. Call the district office (not Washington D.C.) and speak to a staff member. You don't need to be an expert — just say: "Hi, my name is [Name], I'm a constituent from [City], and I'm calling to urge Representative [Name] to support [specific bill or issue]." Staff members tally these calls and report them to the representative.

**Step 4: Request a Meeting**
You can request an in-person or virtual meeting with your representative or their staff. Bring 2–3 people from your community, prepare a one-page summary of your issue, and practice your talking points. These meetings are more common than you think, especially at the local level.

**Step 5: Follow Up and Stay Engaged**
After your meeting or call, send a thank-you email and follow up on any commitments made. Attend town halls. Vote. Bring your friends. Sustained engagement is what turns a single conversation into real policy change.`,
    author: 'Destiny Reyes',
    authorRole: 'Student Writer',
    school: 'George Mason University',
    date: 'Jul 9, 2026',
    readTime: '6 min read',
    tags: ['Local Government', 'Constituent Rights', 'Civic Action'],
  },
  {
    id: 'post-003',
    category: 'Civic Rights',
    type: 'Guide',
    title: 'Know Your Rights: A Youth Guide to Civic Participation',
    excerpt:
      'From voting registration to peaceful assembly, Generation Z has more civic power than many realize. This comprehensive guide covers the rights every young person should know before turning 18 — and after.',
    fullContent: `Generation Z is the most civically engaged generation in recent history — and also one of the most legally protected. Here's what every young person should know about their rights as a participant in American democracy.

**Voting Rights**
If you are a U.S. citizen and 18 years or older, you have the right to vote. In many states, you can pre-register at 16 or 17 so you're automatically registered when you turn 18. Some states allow 17-year-olds to vote in primaries if they will be 18 by the general election. Check your state's rules at vote.gov.

**Freedom of Speech and Assembly**
The First Amendment protects your right to speak, protest, and assemble peacefully — even as a minor. You can attend protests, hold signs, and speak at public meetings. Schools have some authority to regulate speech on campus, but they cannot punish you for political expression outside of school.

**Right to Petition**
You have the right to petition your government at any level — local, state, or federal. This includes circulating petitions, presenting them to officials, and organizing campaigns. Online petitions can be powerful tools for raising awareness, but direct petitions to elected officials carry more legal weight.

**Privacy Rights**
As a minor, you have privacy rights that even your school must respect in certain circumstances. The Fourth Amendment protects you from unreasonable searches and seizures. Schools can search your locker or backpack under certain conditions, but they cannot search your phone without a warrant in most jurisdictions.

**Rights at Protests**
You have the right to film police officers in public. You cannot be arrested for simply being present at a protest. If police order a crowd to disperse, you must comply — but you can ask for the officer's name and badge number. If you are arrested, say clearly: "I am invoking my right to remain silent and my right to an attorney."

Knowing your rights is the first step to using them. Share this guide with your friends and community.`,
    author: 'Jaylen Brooks',
    authorRole: 'Student Writer',
    school: 'Spelman College',
    date: 'Jul 3, 2026',
    readTime: '10 min read',
    tags: ['Voting Rights', 'Assembly Rights', 'Free Speech'],
  },
  {
    id: 'post-004',
    category: 'Immigration Law',
    type: 'Article',
    title: 'Immigration Law 101: What Every Young Person Should Know',
    excerpt:
      'Immigration law shapes the lives of millions of young Americans — whether you\'re a citizen, DACA recipient, or undocumented. Here\'s a plain-language overview of the most important concepts and recent changes.',
    fullContent: `Immigration law is one of the most complex areas of U.S. law — and one of the most consequential for millions of young people. Whether you are a U.S. citizen, a DACA recipient, a visa holder, or undocumented, understanding the basics can help you protect yourself and support your community.

**What is DACA?**
Deferred Action for Childhood Arrivals (DACA) is a federal program that protects certain undocumented individuals who came to the U.S. as children from deportation and allows them to work legally. DACA does not provide a path to citizenship, but it does provide temporary protection. Recipients must renew every two years. As of 2026, DACA continues to face legal challenges, so staying informed is critical.

**Visa Categories**
There are dozens of visa categories in the U.S. immigration system. The most common include: F-1 (student visa), H-1B (skilled worker), B-1/B-2 (tourist/business), and J-1 (exchange visitor). Each has specific rules about work authorization, travel, and duration of stay. Violating the terms of your visa — even unintentionally — can have serious consequences.

**Asylum**
Asylum is a form of protection for people who have been persecuted or fear persecution in their home country based on race, religion, nationality, political opinion, or membership in a particular social group. You must apply for asylum within one year of arriving in the U.S. The process is complex and the backlog is significant — legal representation dramatically improves outcomes.

**Deportation Defense**
If you or a family member receives a deportation order, do not ignore it. Contact an immigration attorney immediately. There are several forms of relief available, including cancellation of removal, adjustment of status, and voluntary departure. Acting quickly is essential.

**Recent Changes**
Immigration policy changes frequently. Stay informed through reliable sources like the American Immigration Lawyers Association (AILA) and the National Immigration Law Center (NILC). Never rely on social media alone for legal information.`,
    author: 'Priya Sharma',
    authorRole: 'Student Writer',
    school: 'University of Texas at Austin',
    date: 'Jun 27, 2026',
    readTime: '12 min read',
    tags: ['DACA', 'Visa Categories', 'Asylum', 'Deportation Defense'],
  },
  {
    id: 'post-005',
    category: 'Advocacy Guides',
    type: 'Analysis',
    title: 'The Political Divide and Generation Z: Why Respectful Discourse Matters',
    excerpt:
      'Political polarization has increased dramatically over the last decade, and Gen Z is bearing the brunt of it. This analysis explores the data, the causes, and what youth advocates can do to bridge the divide.',
    fullContent: `Political polarization in the United States has reached historic levels. According to the Pew Research Center, the ideological gap between Democrats and Republicans has more than doubled since the 1990s. For Generation Z — the first generation to grow up entirely in the social media era — this polarization is not just a political problem. It's a social one.

**The Data**
A 2025 survey found that 67% of Gen Z respondents reported having ended a friendship over political disagreements. Nearly half said they avoid discussing politics with family members. Meanwhile, algorithm-driven social media platforms continue to push users toward increasingly extreme content, creating echo chambers that make genuine dialogue harder.

**Why It Matters**
Democracy depends on the ability of citizens to disagree productively. When we stop talking to people who hold different views, we lose the ability to find common ground, build coalitions, and pass legislation that reflects the full range of public opinion. Polarization doesn't just make politics unpleasant — it makes governance impossible.

**What Gen Z Can Do**
The good news is that Gen Z is also the most likely generation to seek out diverse perspectives when given the tools to do so. Here are three evidence-based strategies for bridging the divide:

1. **Practice Steelmanning**: Before arguing against a position, try to articulate the strongest possible version of it. This forces you to engage with the actual argument rather than a caricature.

2. **Seek Out Structured Dialogue**: Programs like Braver Angels and Living Room Conversations bring people from different political backgrounds together for facilitated discussions. These programs have been shown to reduce hostility and increase mutual understanding.

3. **Focus on Shared Values**: Most people across the political spectrum share core values like fairness, safety, and opportunity. Starting from shared values rather than policy positions makes productive conversation more likely.

The future of American democracy depends on our ability to talk to each other. Gen Z has the tools, the motivation, and the numbers to lead that change.`,
    author: 'Caleb Washington',
    authorRole: 'Student Writer',
    school: 'Northwestern University',
    date: 'Jun 19, 2026',
    readTime: '7 min read',
    tags: ['Political Polarization', 'Civil Discourse', 'Gen Z'],
  },
  {
    id: 'post-006',
    category: 'Legal Aid',
    type: 'Q&A',
    title: 'Q&A: Free Legal Resources for Immigrant Families in the U.S.',
    excerpt:
      'Access to legal representation should not depend on your income. This Q&A with legal aid professionals covers free and low-cost resources available to immigrant families across the country.',
    fullContent: `**Q: I can't afford a lawyer. Does that mean I have to face immigration court alone?**

A: No. While the government is not required to provide free attorneys in immigration cases (unlike criminal cases), there are many organizations that offer free or low-cost legal help. Start with your local legal aid society, which you can find at lawhelp.org. Many law school clinics also take immigration cases at no cost.

**Q: What is "pro bono" legal help?**

A: Pro bono means legal services provided for free or at a significantly reduced rate. Many immigration attorneys are required by their bar associations to provide a certain number of pro bono hours each year. Organizations like the American Immigration Lawyers Association (AILA) maintain pro bono directories. You can also search immigrationadvocates.org for free legal help near you.

**Q: Are there resources specifically for children and unaccompanied minors?**

A: Yes. The Office of Refugee Resettlement (ORR) funds legal representation programs for unaccompanied children. Organizations like Kids in Need of Defense (KIND) and the Young Center for Immigrant Children's Rights specialize in representing minors in immigration proceedings. These services are free.

**Q: What if I'm in immigration detention?**

A: If you are detained, you have the right to contact a lawyer and to make phone calls. Ask the detention facility for a list of free legal service providers. The National Immigration Project and the ACLU Immigrants' Rights Project can also help connect detained individuals with representation.

**Q: Can I get help in languages other than English?**

A: Yes. Most legal aid organizations serving immigrant communities offer services in Spanish, and many offer services in other languages including Portuguese, Mandarin, Vietnamese, and Haitian Creole. When you call, ask about language access.

**Q: What documents should I always have ready?**

A: Keep copies of all immigration documents (visa, I-94, work permit, etc.), your passport, any court notices, and contact information for your attorney in a safe, accessible place. Consider giving a trusted family member or friend copies as well.`,
    author: 'Amara Diallo',
    authorRole: 'Student Writer',
    school: 'Fordham University',
    date: 'Jun 11, 2026',
    readTime: '5 min read',
    tags: ['Legal Aid', 'Pro Bono', 'Immigrant Families'],
  },
];

const legalResources: LegalResource[] = [
  {
    id: 'res-001',
    category: 'Immigration Law',
    title: 'Know Your Rights: Immigration Enforcement Encounters',
    description: 'What to do if you are stopped, questioned, or detained by immigration authorities. Covers your right to remain silent, right to an attorney, and what documents you must show.',
    type: 'Know Your Rights',
    lastUpdated: 'Jul 2026',
    downloads: 1240,
    language: ['English', 'Spanish', 'Portuguese'],
  },
  {
    id: 'res-002',
    category: 'Immigration Law',
    title: 'DACA Renewal Guide 2026',
    description: 'Step-by-step guide to renewing DACA status, including current filing requirements, fee waivers, and what to do if your renewal is denied.',
    type: 'PDF Guide',
    lastUpdated: 'Jun 2026',
    downloads: 892,
    language: ['English', 'Spanish'],
  },
  {
    id: 'res-003',
    category: 'Civic Rights',
    title: 'Voter Registration & Youth Rights Toolkit',
    description: 'Everything you need to register to vote, understand your state\'s laws, and participate in elections as a first-time voter.',
    type: 'PDF Guide',
    lastUpdated: 'May 2026',
    downloads: 2103,
    language: ['English'],
  },
  {
    id: 'res-004',
    category: 'Legal Aid',
    title: 'Free Legal Aid Directory: All 50 States',
    description: 'Comprehensive directory of free and sliding-scale legal aid organizations across the United States, with specializations and contact information.',
    type: 'FAQ',
    lastUpdated: 'Jul 2026',
    downloads: 3481,
    language: ['English', 'Spanish', 'French', 'Mandarin'],
  },
];

const categories = ['All', 'Immigration Law', 'Civic Rights', 'Advocacy Guides', 'Legal Aid'] as const;
type Category = (typeof categories)[number];

const typeColors: Record<string, string> = {
  Article: 'bg-blue-50 text-blue-700',
  Guide: 'bg-emerald-50 text-emerald-700',
  'Q&A': 'bg-purple-50 text-purple-700',
  Analysis: 'bg-amber-50 text-amber-700',
};

const blogLabels = ['All', 'Immigration Law', 'Civic Rights', 'Advocacy Guides', 'Legal Aid', 'Politics'] as const;
type BlogLabel = (typeof blogLabels)[number];

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ResourceContent() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [activeBlogLabel, setActiveBlogLabel] = useState<BlogLabel>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'blog' | 'resources'>('blog');
  const [expandedPost, setExpandedPost] = useState<string | null>(null);

  // Submission form state
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [form, setForm] = useState({
    author_name: '',
    school: '',
    email: '',
    title: '',
    category: 'General',
    content: '',
    link_url: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const wordCount = form.content.trim() === '' ? 0 : form.content.trim().split(/\s+/).length;

  const featuredPost = blogPosts.find((p) => p.featured)!;

  const filteredPosts = blogPosts
    .filter((p) => !p.featured)
    .filter((p) => {
      const matchesLabel = activeBlogLabel === 'All' || p.category === activeBlogLabel;
      const matchesSearch =
        searchQuery === '' ||
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesLabel && matchesSearch;
    });

  const filteredResources = legalResources.filter((r) => {
    const matchesCat = activeCategory === 'All' || r.category === activeCategory;
    const matchesSearch =
      searchQuery === '' ||
      r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (wordCount > 500) return;
    setSubmitStatus('submitting');
    setFileError(null);
    try {
      const supabase = createClient();

      let file_url: string | null = null;

      // Upload file if selected
      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('blog-submissions')
          .upload(fileName, selectedFile, { upsert: false });
        if (uploadError) {
          setFileError('File upload failed: ' + uploadError.message);
          setSubmitStatus('error');
          return;
        }
        file_url = uploadData?.path ?? null;
      }

      const payload: Record<string, string | null> = {
        author_name: form.author_name,
        school: form.school,
        email: form.email,
        title: form.title,
        category: form.category,
        content: form.content,
        link_url: form.link_url.trim() || null,
        file_url,
      };

      const { error } = await supabase.from('blog_submissions').insert([payload]);
      if (error) throw error;
      setSubmitStatus('success');
      setForm({ author_name: '', school: '', email: '', title: '', category: 'General', content: '', link_url: '' });
      setSelectedFile(null);
    } catch {
      setSubmitStatus('error');
    }
  }

  return (
    <section className="py-12 bg-zinc-50">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        {/* Featured article */}
        <FeaturedArticle post={featuredPost} typeColors={typeColors} onExpand={() => setExpandedPost(expandedPost === featuredPost.id ? null : featuredPost.id)} expanded={expandedPost === featuredPost.id} />

        {/* Search + Tab switcher */}
        <div className="mt-12 mb-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Icon
                name="MagnifyingGlassIcon"
                size={16}
                variant="outline"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <input
                type="text"
                placeholder={activeTab === 'blog' ? 'Search blog posts...' : 'Search legal resources...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-base pl-9"
                suppressHydrationWarning
              />
            </div>
            {/* Tab switcher */}
            <div className="flex items-center bg-white border border-border rounded-lg p-1 gap-1">
              <button
                onClick={() => { setActiveTab('blog'); setSearchQuery(''); }}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all duration-150 ${
                  activeTab === 'blog' ? 'crimson-gradient text-white shadow-sm' : 'text-zinc-600 hover:text-foreground'
                }`}
                suppressHydrationWarning
              >
                Blog Posts
              </button>
              <button
                onClick={() => { setActiveTab('resources'); setSearchQuery(''); }}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all duration-150 ${
                  activeTab === 'resources' ? 'crimson-gradient text-white shadow-sm' : 'text-zinc-600 hover:text-foreground'
                }`}
                suppressHydrationWarning
              >
                Legal Resources
              </button>
            </div>
          </div>

          {/* Blog section: description banner + label filters */}
          {activeTab === 'blog' && (
            <>
              {/* Blog description banner */}
              <div className="mb-6 rounded-2xl border border-primary/20 bg-white overflow-hidden">
                <div className="bg-gradient-to-r from-primary/8 to-rose-50 border-b border-primary/10 px-6 py-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl crimson-gradient flex items-center justify-center flex-shrink-0">
                      <Icon name="NewspaperIcon" size={20} variant="outline" className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base font-extrabold text-foreground">Student Blog Posts</h3>
                        <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">Reviewed &amp; Researched</span>
                        <span className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">Open Weekly</span>
                      </div>
                      <p className="text-sm text-zinc-600 leading-relaxed">
                        Every blog post is written by a student, reviewed by our team, and selected for quality and accuracy before being published here. We cover topics across <strong>law, politics, civic rights, immigration</strong>, and more.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50/60">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Icon name="ClockIcon" size={15} variant="solid" className="text-amber-600" />
                    <span className="text-xs font-bold text-amber-800">Submission Deadline</span>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    New submissions are accepted <strong>every week</strong>. The deadline is <strong>every Sunday at 9:00 PM</strong>. All responses are reviewed and the best-written submission is selected to be featured on this website.
                  </p>
                </div>
              </div>

              {/* Blog label filter buttons */}
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
                {blogLabels.map((label) => (
                  <button
                    key={`blog-label-${label}`}
                    onClick={() => setActiveBlogLabel(label)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-150 ${
                      activeBlogLabel === label
                        ? 'crimson-gradient text-white shadow-sm'
                        : 'bg-white text-zinc-600 border border-border hover:border-primary hover:text-primary'
                    }`}
                    suppressHydrationWarning
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Legal Resources section: source note + category filters */}
          {activeTab === 'resources' && (
            <>
              {/* Legal resources source note */}
              <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-4 flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Icon name="ShieldCheckIcon" size={18} variant="solid" className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-emerald-900 mb-0.5">Verified Legal Sources</p>
                  <p className="text-xs text-emerald-800 leading-relaxed">
                    All resources in this section are sourced from <strong>reviewed and authoritative legal organizations</strong> — including federal agencies, accredited legal aid societies, bar associations, and established nonprofit legal advocacy groups. These are not student-written materials.
                  </p>
                </div>
              </div>

              {/* Category chips for legal resources */}
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
                {categories.map((cat) => (
                  <button
                    key={`res-cat-${cat}`}
                    onClick={() => setActiveCategory(cat)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-150 ${
                      activeCategory === cat
                        ? 'crimson-gradient text-white shadow-sm'
                        : 'bg-white text-zinc-600 border border-border hover:border-primary hover:text-primary'
                    }`}
                    suppressHydrationWarning
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        {activeTab === 'blog' ? (
          filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <BlogPostCard
                  key={post.id}
                  post={post}
                  typeColors={typeColors}
                  expanded={expandedPost === post.id}
                  onExpand={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mb-4">
                <Icon name="DocumentTextIcon" size={28} variant="outline" className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">No articles found</h3>
              <p className="text-sm text-muted-foreground max-w-xs mb-4">
                No blog posts match your search or filter. Try different keywords.
              </p>
              <button
                onClick={() => { setActiveBlogLabel('All'); setSearchQuery(''); }}
                className="btn-secondary"
                suppressHydrationWarning
              >
                Clear Filters
              </button>
            </div>
          )
        ) : filteredResources.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-6">
            {filteredResources.map((res) => (
              <LegalResourceCard key={res.id} resource={res} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mb-4">
              <Icon name="FolderOpenIcon" size={28} variant="outline" className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">No resources found</h3>
            <p className="text-sm text-muted-foreground max-w-xs mb-4">
              No legal resources match your current filters.
            </p>
            <button
              onClick={() => { setActiveCategory('All'); setSearchQuery(''); }}
              className="btn-secondary"
              suppressHydrationWarning
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* ── Student Submission Section ── */}
        <div className="mt-16 rounded-2xl border border-primary/20 bg-white overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-b border-primary/10 px-8 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl crimson-gradient flex items-center justify-center flex-shrink-0">
                <Icon name="PencilSquareIcon" size={22} variant="outline" className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-extrabold text-foreground">Submit Your Article</h2>
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">Open Every Week</span>
                </div>
                <p className="text-sm text-zinc-600 max-w-2xl">
                  We accept new student-written submissions <strong>every week</strong>. Share your perspective on law, civic rights, immigration, or advocacy. Articles must be original, respectful, and up to 500 words.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="px-8 py-8">
            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-emerald-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Submission Received!</h3>
                <p className="text-sm text-zinc-500 max-w-sm mb-6">
                  Thank you for contributing to the Crimson Law Resource Hub. Our team will review your article and be in touch.
                </p>
                <button onClick={() => setSubmitStatus('idle')} className="btn-secondary" suppressHydrationWarning>
                  Submit Another Article
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Your Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      required
                      value={form.author_name}
                      onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                      placeholder="First and last name"
                      className="input-base w-full"
                      suppressHydrationWarning
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">School / University <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      required
                      value={form.school}
                      onChange={(e) => setForm({ ...form, school: e.target.value })}
                      placeholder="e.g. Lincoln High School or State University"
                      className="input-base w-full"
                      suppressHydrationWarning
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Email Address <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="your@email.com"
                      className="input-base w-full"
                      suppressHydrationWarning
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Category <span className="text-red-500">*</span></label>
                    <select
                      required
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="input-base w-full cursor-pointer"
                      suppressHydrationWarning
                    >
                      <option value="General">General</option>
                      <option value="Immigration Law">Immigration Law</option>
                      <option value="Civic Rights">Civic Rights</option>
                      <option value="Advocacy Guides">Advocacy Guides</option>
                      <option value="Legal Aid">Legal Aid</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Article Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Give your article a clear, descriptive title"
                    className="input-base w-full"
                    suppressHydrationWarning
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-foreground">Article Content <span className="text-red-500">*</span></label>
                    <span className={`text-xs font-semibold ${wordCount > 500 ? 'text-red-500' : wordCount > 400 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                      {wordCount} / 500 words
                    </span>
                  </div>
                  <textarea
                    required
                    rows={10}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder="Write your article here (up to 500 words). Focus on a specific legal topic, civic issue, or advocacy strategy relevant to young people."
                    className="input-base w-full resize-none"
                    suppressHydrationWarning
                  />
                  {wordCount > 500 && (
                    <p className="text-xs text-red-500 mt-1">Please reduce your article to 500 words or fewer.</p>
                  )}
                </div>

                {/* Attachments section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Attach a File <span className="text-zinc-400 font-normal">(optional)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer border border-dashed border-zinc-300 rounded-xl px-4 py-3 hover:border-primary/50 hover:bg-primary/5 transition-colors">
                      <Icon name="PaperClipIcon" size={16} variant="outline" className="text-zinc-400 shrink-0" />
                      <span className="text-sm text-zinc-500 truncate">
                        {selectedFile ? selectedFile.name : 'PDF, DOC, DOCX, TXT, JPG, PNG (max 10MB)'}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                        className="sr-only"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          if (file && file.size > 10 * 1024 * 1024) {
                            setFileError('File must be under 10MB.');
                            setSelectedFile(null);
                          } else {
                            setFileError(null);
                            setSelectedFile(file);
                          }
                        }}
                        suppressHydrationWarning
                      />
                    </label>
                    {selectedFile && (
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="mt-1.5 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                        suppressHydrationWarning
                      >
                        <Icon name="XMarkIcon" size={12} variant="outline" />
                        Remove file
                      </button>
                    )}
                    {fileError && <p className="text-xs text-red-500 mt-1">{fileError}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Reference Link <span className="text-zinc-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="url"
                      value={form.link_url}
                      onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                      placeholder="https://example.com/your-article"
                      className="input-base w-full"
                      suppressHydrationWarning
                    />
                    <p className="text-xs text-zinc-400 mt-1">Link to a Google Doc, external article, or reference</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <Icon name="InformationCircleIcon" size={16} variant="solid" className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    By submitting, you confirm this is your original work and agree to have it published on the Crimson Law Resource Hub with your name and school credited. Submissions are reviewed by our team before publishing.
                  </p>
                </div>
                {submitStatus === 'error' && (
                  <p className="text-sm text-red-600 font-medium">Something went wrong. Please try again.</p>
                )}
                <button
                  type="submit"
                  disabled={submitStatus === 'submitting' || wordCount > 500 || wordCount === 0}
                  className="btn-primary px-8 disabled:opacity-50 disabled:cursor-not-allowed"
                  suppressHydrationWarning
                >
                  {submitStatus === 'submitting' ? (
                    <>
                      <Icon name="ArrowPathIcon" size={15} variant="outline" className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Icon name="PaperAirplaneIcon" size={15} variant="outline" />
                      Submit Article
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}